import { css, cx } from '@emotion/css';
import { darken } from '@mui/material';
import {
  BaseEdge,
  Edge,
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
  Position,
  SmoothStepEdge,
} from '@xyflow/react';
import { ReactNode, useId, useMemo } from 'react';

import { COLORS } from '@/styles/tokens/colors';
import { isReactNode } from '@/utils/reactUtils';

import {
  EdgeLabel as EdgeLabelExternal,
  EdgeLabelProps,
} from '../../components/EdgeLabel';
import {
  type ArrowEdgeRoute,
  type ArrowEdgeStyleVariant,
  type ArrowEdgeVariant,
  DiagramEdgeLineStyle,
  DiagramEdgeRoute,
} from '../../diagramStyle';
import { useIsConnectedNodeDragging } from '../hooks/useIsConnectedNodeDragging';
import { useMarkerId } from '../hooks/useMarkerId';
import { MarkerType, MarkerTypeToProps } from '../types';
import {
  filterCrossingsForRoundedPath,
  getUnroundedSmoothStepPoints,
  ORTHOGONAL_CROSSING_GAP_RADIUS,
} from './crossingGaps';
import { useLiveEdgeCrossings } from './LiveEdgeCrossings';
import {
  buildRoundedOrthogonalPath,
  getOrthogonalPathLabelPoint,
  OrthogonalPathPoint,
  snapEndpointRailsToHandles,
} from './orthogonalPath';
import { getRequestedEdgePath } from './requestedEdgePath';

const styles = {
  labelContainer: css({
    // everything inside EdgeLabelRenderer has no pointer events by default
    // if you have an interactive element, set pointer-events: all
    // https://reactflow.dev/api-reference/components/edge-label-renderer#notes
    // https://github.com/xyflow/xyflow/issues/3303
    pointerEvents: 'all',
    position: 'absolute',
    cursor: 'default',
  }),
};

export type { ArrowEdgeStyleVariant, ArrowEdgeVariant };

export interface ArrowProps extends Edge {
  data: {
    variant?: ArrowEdgeVariant;
    /** An opaque hex color. It replaces the color of `variant`. */
    customColor?: string;
    style?: ArrowEdgeStyleVariant;
    hideLabel?: boolean;
    hideArrow?: boolean;
    /**
     * By default, the arrow is only shown on the destination.
     * If this is true, the arrow will be shown pointing back to the source as well.
     * This is useful for e.g. showing a loan being paid back.
     */
    showFromArrow?: boolean;
    /**
     * Hides the arrow at the destination. With `showFromArrow`, the line has
     * an arrow only at its source.
     */
    hideDestinationArrow?: boolean;
    edgeLabel?: EdgeLabelProps | ReactNode;
    highlight?: boolean;
    /**
     * By default, the edges will break halfway along their y travel.
     * If centerFactor is provided, the edge will break in accordance with the
     * centerFactor ratio. 0.5 is the default, 0.8 is closer to the target node,
     * 0.2 is closer to the source node.
     */
    centerFactor?: number;
    /**
     * If provided, a downward edge breaks a fixed distance (px) above its
     * target instead of halfway along its y travel. Edges converging on a
     * node then merge just above it, keeping each parent's own vertical
     * segment visible. Ignored when the edge is too short for the break to
     * leave a matching stub below the source, or when centerFactor is set.
     */
    breakDistanceFromTarget?: number;
    /** Path shape between the handles. Defaults to an elbow path. */
    route?: ArrowEdgeRoute;
    /**
     * Caller-owned absolute interior points. The path passes through them
     * with the `route` shape. They replace `routeWaypoints`.
     */
    waypoints?: OrthogonalPathPoint[];
    /** Line width in canvas pixels. Defaults to 1. */
    strokeWidth?: number;
    /** Absolute interior corners for a static orthogonal route. */
    routeWaypoints?: OrthogonalPathPoint[];
    /** Router-owned label center. Omit when the routed node positions change. */
    labelPosition?: OrthogonalPathPoint;
    /** Straight crossing or unrounded bend coordinates that need a gap. */
    routeCrossings?: OrthogonalPathPoint[];
    /** Router-owned stubs that can shrink to fit a tight label-to-tile gap. */
    routedEndpointStubs?: { source: number; target: number };
    /**
     * Override the marker type used at the end of the edge.
     * Defaults to 'arrow'. Use 'circle' for a filled dot terminator.
     */
    endMarkerType?: MarkerType;
    /**
     * Override the smoothstep corner radius. The grid layout sets a smaller
     * value so short and long edges get matching corners.
     */
    borderRadius?: number;
    /** Override the smoothstep handle offset (straight stub out of each node). */
    offset?: number;
  };
}

export type ArrowEdgeProps = EdgeProps<ArrowProps>;

// TODO: Need a better way of theming this since props need to come from parent in useEdges.ts,
// see useEdges.ts for more context
export const ARROW_EDGE_COLORS_BY_VARIANT: Record<ArrowEdgeVariant, string> = {
  primary: COLORS.GRAY[500],
  secondary: COLORS.FUNCTIONAL.WARNING[400],
  destructive: COLORS.ORANGE[400],
  accent: COLORS.FUNCTIONAL.SUCCESS[600],
};

/** Gap between the dots of a dotted line, in multiples of the line width. */
const DOT_GAP_TO_WIDTH = 3;

/** Returns the SVG dash pattern of a line style, or undefined for solid. */
function getDashArray(
  lineStyle: ArrowEdgeStyleVariant,
  strokeWidth: number
): string | undefined {
  switch (lineStyle) {
    case DiagramEdgeLineStyle.Solid:
      return undefined;
    case DiagramEdgeLineStyle.Dashed:
      return '5 5';
    case DiagramEdgeLineStyle.Dotted:
      // A zero-length dash with a round cap draws a dot as wide as the line.
      return `0 ${strokeWidth * DOT_GAP_TO_WIDTH}`;
  }
}

export const CENTER_FACTOR_ARROW_EDGE_BORDER_RADIUS = 16;
export const CENTER_FACTOR_ARROW_EDGE_OFFSET = 16;

export const ARROW_EDGE_COLORS_BY_VARIANT_DRAGGING_OR_HIGHLIGHT: Record<
  ArrowEdgeVariant,
  string
> = {
  primary: COLORS.GRAY[700],
  secondary: COLORS.FUNCTIONAL.WARNING[700],
  destructive: COLORS.ORANGE[700],
  accent: COLORS.FUNCTIONAL.SUCCESS[800],
};

/**
 * The vertical travel sign at one end of a routed path, derived from the
 * handle side. A source on a bottom handle exits downward (+1); a target on
 * a top handle is entered downward (+1). Horizontal handles return
 * undefined so the snap falls back to its heuristic.
 */
function verticalTravelSign(
  position: Position | undefined,
  end: 'source' | 'target'
): 1 | -1 | undefined {
  if (position === Position.Bottom) {
    return end === 'source' ? 1 : -1;
  }
  if (position === Position.Top) {
    return end === 'source' ? -1 : 1;
  }
  return undefined;
}

export function useArrowEdgeColor({
  source,
  target,
  data = {},
}: ArrowEdgeProps) {
  const { variant = 'primary', customColor, highlight = false } = data;
  const { dragging } = useIsConnectedNodeDragging({ source, target });
  const isEmphasized = dragging || highlight;
  if (customColor) {
    return isEmphasized ? darken(customColor, 0.3) : customColor;
  }
  return isEmphasized
    ? ARROW_EDGE_COLORS_BY_VARIANT_DRAGGING_OR_HIGHLIGHT[variant]
    : ARROW_EDGE_COLORS_BY_VARIANT[variant];
}

export function ArrowEdge(props: ArrowEdgeProps) {
  const { style: externalStyle, data: inputData, ...rest } = props;
  const data = useMemo(() => inputData ?? {}, [inputData]);
  const {
    hideLabel,
    hideArrow,
    edgeLabel,
    highlight,
    showFromArrow,
    hideDestinationArrow,
    style: edgeStyle = 'solid',
    centerFactor,
    breakDistanceFromTarget,
    route = DiagramEdgeRoute.Elbow,
    waypoints,
    strokeWidth = 1,
    routeWaypoints,
    routeCrossings,
    routedEndpointStubs,
    endMarkerType = 'arrow',
    borderRadius = CENTER_FACTOR_ARROW_EDGE_BORDER_RADIUS,
    offset,
  } = data;
  const color = useArrowEdgeColor(props);
  const crossingMaskId = useId().replace(/:/g, '');

  const style: ArrowEdgeProps['style'] = useMemo(() => {
    return {
      stroke: color,
      strokeWidth: `${strokeWidth}px`,
      strokeDasharray: getDashArray(edgeStyle, strokeWidth),
      strokeLinecap:
        edgeStyle === DiagramEdgeLineStyle.Dotted ? 'round' : undefined,
      ...externalStyle,
    };
  }, [color, edgeStyle, strokeWidth, externalStyle]);

  const requestedPath = useMemo(
    () =>
      getRequestedEdgePath({
        route,
        source: { x: props.sourceX, y: props.sourceY },
        target: { x: props.targetX, y: props.targetY },
        sourcePosition: props.sourcePosition,
        targetPosition: props.targetPosition,
        waypoints: waypoints ?? [],
        borderRadius,
      }),
    [
      route,
      waypoints,
      borderRadius,
      props.sourceX,
      props.sourceY,
      props.targetX,
      props.targetY,
      props.sourcePosition,
      props.targetPosition,
    ]
  );

  const centerY = useMemo(() => {
    const heightOfYTravel = props.targetY - props.sourceY;

    if (centerFactor) {
      // Controls how close to the target node the edge breaks
      return props.sourceY + heightOfYTravel * centerFactor;
    }

    if (breakDistanceFromTarget !== undefined) {
      const brokenY = props.targetY - breakDistanceFromTarget;
      // Only break near the target when an equally long stub remains below
      // the source; shorter edges keep the default midpoint break.
      if (brokenY >= props.sourceY + breakDistanceFromTarget) {
        return brokenY;
      }
    }

    return undefined;
  }, [centerFactor, breakDistanceFromTarget, props.sourceY, props.targetY]);

  const routedPathPoints = useMemo<OrthogonalPathPoint[] | null>(() => {
    if (requestedPath || !routeWaypoints || routeWaypoints.length === 0) {
      return null;
    }
    // The waypoints come from route estimates of node geometry, while the
    // handle points are measured after render. Snap end rails that land past
    // a handle so the path cannot reverse and flip the arrowhead. The handle
    // sides give the true travel direction at each end; on a wrapped route
    // the opposite-handle heuristic points the wrong way.
    return snapEndpointRailsToHandles(
      [
        { x: props.sourceX, y: props.sourceY },
        ...routeWaypoints,
        { x: props.targetX, y: props.targetY },
      ],
      {
        source: verticalTravelSign(props.sourcePosition, 'source'),
        target: verticalTravelSign(props.targetPosition, 'target'),
      },
      routedEndpointStubs
    );
  }, [
    requestedPath,
    routeWaypoints,
    routedEndpointStubs,
    props.sourceX,
    props.sourceY,
    props.targetX,
    props.targetY,
    props.sourcePosition,
    props.targetPosition,
  ]);

  const [smoothStepPath, smoothStepLabelX, smoothStepLabelY] = useMemo(
    () =>
      getSmoothStepPath({
        ...props,
        centerY,
        borderRadius,
        // Match the drawn SmoothStepEdge so its label follows the same path.
        offset: offset ?? CENTER_FACTOR_ARROW_EDGE_OFFSET,
      }),
    [props, centerY, borderRadius, offset]
  );
  let edgePath = smoothStepPath;
  if (requestedPath) {
    edgePath = requestedPath.path;
  } else if (routedPathPoints) {
    edgePath = buildRoundedOrthogonalPath(routedPathPoints, borderRadius);
  }
  const crossingPathPoints = useMemo(() => {
    if (routedPathPoints) return routedPathPoints;
    const [path] = getSmoothStepPath({
      sourceX: props.sourceX,
      sourceY: props.sourceY,
      targetX: props.targetX,
      targetY: props.targetY,
      sourcePosition: props.sourcePosition,
      targetPosition: props.targetPosition,
      centerY,
      borderRadius: 0,
      offset: offset ?? CENTER_FACTOR_ARROW_EDGE_OFFSET,
    });
    return getUnroundedSmoothStepPoints(path);
  }, [
    routedPathPoints,
    props.sourceX,
    props.sourceY,
    props.targetX,
    props.targetY,
    props.sourcePosition,
    props.targetPosition,
    centerY,
    offset,
  ]);
  // A present but empty crossing field opts in before the first intersection.
  const showCrossings = Object.hasOwn(data, 'routeCrossings');
  const liveCrossings = useLiveEdgeCrossings(
    props.id,
    crossingPathPoints,
    showCrossings,
    routeCrossings,
    borderRadius
  );
  const visibleCrossings = useMemo(
    () =>
      liveCrossings
        ? filterCrossingsForRoundedPath(
            liveCrossings,
            crossingPathPoints,
            borderRadius
          )
        : [],
    [borderRadius, liveCrossings, crossingPathPoints]
  );
  const maskBounds = useMemo(() => {
    const points = crossingPathPoints;
    const padding = borderRadius + ORTHOGONAL_CROSSING_GAP_RADIUS * 2;
    const left = Math.min(...points.map((point) => point.x)) - padding;
    const top = Math.min(...points.map((point) => point.y)) - padding;
    return {
      x: left,
      y: top,
      width: Math.max(...points.map((point) => point.x)) + padding - left,
      height: Math.max(...points.map((point) => point.y)) + padding - top,
    };
  }, [borderRadius, crossingPathPoints]);
  let labelPoint = { x: smoothStepLabelX, y: smoothStepLabelY };
  if (requestedPath) {
    labelPoint = requestedPath.label;
  } else if (routedPathPoints) {
    labelPoint = getOrthogonalPathLabelPoint(routedPathPoints);
  }
  const { x: labelX, y: labelY } = data.labelPosition ?? labelPoint;

  const arrowMarkerProps = useMemo<MarkerTypeToProps['arrow']>(
    () => ({ color }),
    [color]
  );
  const circleMarkerProps = useMemo<MarkerTypeToProps['circle']>(
    () => ({ color }),
    [color]
  );
  const arrowMarkerId = useMarkerId('arrow', arrowMarkerProps);
  const circleMarkerId = useMarkerId('circle', circleMarkerProps);
  const destinationMarkerId =
    endMarkerType === 'circle' ? circleMarkerId : arrowMarkerId;
  const sourceMarkerId = useMarkerId('arrow', {
    ...arrowMarkerProps,
    direction: 'reverse',
  });
  let EdgeLabelComponent: ReactNode = null;
  if (isReactNode(edgeLabel)) {
    EdgeLabelComponent = edgeLabel;
  } else if (edgeLabel?.label || edgeLabel?.label === '') {
    // accept an empty string so the value can be shown sans label
    EdgeLabelComponent = (
      <EdgeLabelExternal {...edgeLabel} highlight={highlight} />
    );
  }

  const EdgeLabel = useMemo(() => {
    if (!EdgeLabelComponent) {
      return null;
    }

    return (
      <EdgeLabelRenderer>
        <div
          className={cx(styles.labelContainer, 'nodrag', 'nopan')}
          // The diagram renderer measures the drawn label box by edge ID.
          data-edge-label-id={props.id}
          style={{
            opacity: hideLabel ? 0 : 1,
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          {EdgeLabelComponent}
        </div>
      </EdgeLabelRenderer>
    );
  }, [EdgeLabelComponent, hideLabel, labelX, labelY, props.id]);

  const commonProps = useMemo(() => {
    return {
      ...rest,
      style,
      markerEnd:
        hideArrow || hideDestinationArrow ? undefined : destinationMarkerId,
      markerStart: showFromArrow && !hideArrow ? sourceMarkerId : undefined,
    };
  }, [
    rest,
    style,
    hideArrow,
    hideDestinationArrow,
    destinationMarkerId,
    showFromArrow,
    sourceMarkerId,
  ]);
  // Keep the Chrome 118 remount workaround for disappearing arrows (T1-521).
  // Remount when the rendered arrow changes, not on unchanged parent renders.
  const edgeKey = JSON.stringify([
    edgePath,
    offset,
    style,
    commonProps.markerEnd,
    commonProps.markerStart,
    visibleCrossings,
  ]);

  // Render a custom path when the route or break point is overridden.
  if (
    requestedPath ||
    routedPathPoints ||
    centerY !== undefined ||
    showCrossings
  ) {
    return (
      <>
        {visibleCrossings.length > 0 && (
          <defs>
            <mask
              id={crossingMaskId}
              maskUnits="userSpaceOnUse"
              {...maskBounds}
            >
              <rect {...maskBounds} fill="white" />
              {visibleCrossings.map(({ x, y, curve }) =>
                curve ? (
                  <path
                    key={curve}
                    d={curve}
                    fill="none"
                    stroke="black"
                    strokeWidth={ORTHOGONAL_CROSSING_GAP_RADIUS * 2}
                    strokeLinecap="round"
                  />
                ) : (
                  <circle
                    key={`${x}:${y}`}
                    cx={x}
                    cy={y}
                    r={ORTHOGONAL_CROSSING_GAP_RADIUS}
                    fill="black"
                  />
                )
              )}
            </mask>
          </defs>
        )}
        <BaseEdge
          key={edgeKey}
          id={commonProps.id}
          interactionWidth={commonProps.interactionWidth}
          style={commonProps.style}
          markerEnd={commonProps.markerEnd}
          markerStart={commonProps.markerStart}
          label={commonProps.label}
          labelStyle={commonProps.labelStyle}
          labelShowBg={commonProps.labelShowBg}
          labelBgStyle={commonProps.labelBgStyle}
          labelBgPadding={commonProps.labelBgPadding}
          labelBgBorderRadius={commonProps.labelBgBorderRadius}
          path={edgePath}
          mask={
            visibleCrossings.length > 0 ? `url(#${crossingMaskId})` : undefined
          }
        />
        {EdgeLabel}
      </>
    );
  }

  return (
    <>
      <SmoothStepEdge
        key={edgeKey}
        {...commonProps}
        pathOptions={{ borderRadius, offset }}
      />
      {EdgeLabel}
    </>
  );
}
